import {
  alpha,
  Autocomplete,
  Box,
  Chip,
  SxProps,
  TextField,
  TextFieldProps,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Theme } from '@mui/system';
import { debounce } from 'lodash';
import { ReactNode, useCallback } from 'react';
import { Search } from 'react-feather';

// Components
import IconFeather from 'src/components/common/Icon';
import PaperSelect from 'src/components/common/PaperSelect';
import TooltipFormLabel from 'src/components/common/TooltipFormLabel';

interface IProps {
  id: string;
  defaultKeyword?: string;
  required?: boolean;
  tooltipText?: string;
  label?: ReactNode;
  disabled?: boolean;
  isError?: boolean;
  value: any;
  inputValue: string;
  onChange: any;
  onTextInputChange?: any;
  options: any[];
  loading?: boolean;
  onBlur?: any;
  inputProps?: TextFieldProps;
  sx?: SxProps<Theme>;
}

const FormSearchHsCodeByAI = ({
  id,
  tooltipText = '',
  label,
  required = false,
  disabled = false,
  isError = false,
  value,
  onChange,
  onTextInputChange,
  options,
  loading,
  inputValue,
  onBlur,
  inputProps,
  sx,
}: IProps) => {
  const theme = useTheme();
  const handleTextInputChange = useCallback(
    debounce((e) => {
      onTextInputChange(e.target.value);
    }, 1000),
    [],
  );

  return (
    <Autocomplete
      sx={sx}
      value={value}
      onChange={(_, value) => onChange(value)}
      loading={loading}
      onBlur={onBlur}
      id={id}
      fullWidth
      size="small"
      freeSolo
      filterOptions={(option) => option}
      autoComplete
      loadingText={'AI is predicting your HS codes...'}
      options={options}
      disabled={disabled}
      popupIcon={<IconFeather muiProps={{ fontSize: 'small' }} icon={<Search />} />}
      PaperComponent={PaperSelect}
      autoHighlight
      getOptionLabel={(option) => {
        return option ? `${option.hs_code}${option.description ? `- ${option.description}` : ''}` : '';
      }}
      renderOption={(props, option) => (
        <Tooltip title={option?.description || ''}>
          <Box gap={1} display={'flex'} component="li" alignItems="flex-start" {...props}>
            <Chip
              label={option.hs_code}
              color="success"
              variant="filled"
              sx={{
                '&:hover': {
                  color: theme.palette.success.main,
                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                },
              }}
            />
            <Typography
              sx={{
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: '2',
                lineClamp: '2',
                WebkitBoxOrient: 'vertical',
              }}
            >
              {option?.description || ''}
            </Typography>
          </Box>
        </Tooltip>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          {...inputProps}
          id={`${id}-text`}
          error={isError}
          required={required}
          value={inputValue}
          InputLabelProps={{
            ...params.InputLabelProps,
          }}
          placeholder="Typing..."
          onChange={handleTextInputChange}
          label={label ? label : <span>Description {tooltipText && <TooltipFormLabel title={tooltipText} />}</span>}
        />
      )}
    />
  );
};

export default FormSearchHsCodeByAI;
